import {
  IProcedureCopayListItem,
  IProcedureCopayListItemSaveRequest,
  IProcedureCopayListResolution,
  IProcedureCopayListResolveProcedure,
  IProcedureCopayListView,
} from "types/ProcedureCopayList/ProcedureCopayList";

import { api } from ".";

/**
 * CLE-2816 PR 2 — the CPT → copay-list item mapping the benefit engine reads for UHC's "AMOUNT
 * VARIES BY LOCATION" copay list. Eligibility › Copay list. SuperAdmin only, every endpoint.
 *
 * Rows are addressed by CODE: while nothing is saved the list is the built-in one, whose rows have
 * no id. The API writes the whole built-in list on the first save.
 *
 * RESOLVE: the tab's tester. A POST that only reads, so it is a QUERY (like the CPT groups'
 * classify), called through the LAZY hook, whose trigger always refetches. Every write invalidates
 * the list and the resolve answers; the API's own cache is invalidated by the same save.
 */
const apiWithProcedureCopayListTags = api.enhanceEndpoints({
  addTagTypes: ["ProcedureCopayListItems"],
});

export const PROCEDURE_COPAY_LIST_PATH =
  "eligibility/procedure-copay-list-items";

const LIST_TAG = { type: "ProcedureCopayListItems" as const, id: "LIST" };

export const procedureCopayListQueries =
  apiWithProcedureCopayListTags.injectEndpoints({
    endpoints: (builder) => ({
      getProcedureCopayListItems: builder.query<IProcedureCopayListView, void>({
        query: () => PROCEDURE_COPAY_LIST_PATH,
        transformResponse: (response: { data: IProcedureCopayListView }) =>
          response.data,
        providesTags: [LIST_TAG],
      }),
      resolveProcedureCopayList: builder.query<
        IProcedureCopayListResolution,
        IProcedureCopayListResolveProcedure[]
      >({
        query: (procedures) => ({
          url: `${PROCEDURE_COPAY_LIST_PATH}/resolve`,
          method: "POST",
          body: { procedures },
        }),
        transformResponse: (response: {
          data: IProcedureCopayListResolution;
        }) => response.data,
        providesTags: [LIST_TAG],
      }),
      createProcedureCopayListItem: builder.mutation<
        IProcedureCopayListItem,
        IProcedureCopayListItemSaveRequest
      >({
        query: (body) => ({
          url: PROCEDURE_COPAY_LIST_PATH,
          method: "POST",
          body,
        }),
        transformResponse: (response: { data: IProcedureCopayListItem }) =>
          response.data,
        invalidatesTags: [LIST_TAG],
      }),
      updateProcedureCopayListItem: builder.mutation<
        IProcedureCopayListItem,
        { cptCode: string; body: IProcedureCopayListItemSaveRequest }
      >({
        query: ({ cptCode, body }) => ({
          url: `${PROCEDURE_COPAY_LIST_PATH}/${encodeURIComponent(cptCode)}`,
          method: "PUT",
          body,
        }),
        transformResponse: (response: { data: IProcedureCopayListItem }) =>
          response.data,
        invalidatesTags: [LIST_TAG],
      }),
      deleteProcedureCopayListItem: builder.mutation<
        { cptCode: string },
        string
      >({
        query: (cptCode) => ({
          url: `${PROCEDURE_COPAY_LIST_PATH}/${encodeURIComponent(cptCode)}`,
          method: "DELETE",
        }),
        transformResponse: (response: { data: { cptCode: string } }) =>
          response.data,
        invalidatesTags: [LIST_TAG],
      }),
    }),
  });

export const {
  useGetProcedureCopayListItemsQuery,
  useLazyResolveProcedureCopayListQuery,
  useCreateProcedureCopayListItemMutation,
  useUpdateProcedureCopayListItemMutation,
  useDeleteProcedureCopayListItemMutation,
} = procedureCopayListQueries;
